import {
    CaretRightOutlined,
    FullscreenExitOutlined,
    FullscreenOutlined,
    LoadingOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined
} from '@ant-design/icons';
import { FloatButton, InputNumber, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCalculationJob, type CalculationType } from './useCalculationJob';

/**
 * Charge / multiplicity inputs plus the Gaussian job launcher.
 *
 * This is the part of the JSmol pane that has nothing to do with rendering: it
 * feeds `/calculation/start` and opens the output tab. Kept as an overlay so the
 * new viewer is a drop-in replacement for the old iframe.
 */

const CALCULATIONS: Array<{ type: CalculationType; label: React.ReactNode }> = [
    { type: 'optimize', label: 'opt' },
    { type: 'TSopt', label: 'TSopt' },
    { type: 'freq', label: 'freq' },
    { type: 'optAndFreq', label: <span style={{ lineHeight: 0.8 }}>opt<br />+<br />freq</span> },
    { type: 'ir', label: 'IR' },
    { type: 'uvvis', label: 'UV/vis' },
    { type: 'raman', label: 'Raman' }
];

export interface ChargeMultiplicityCardProps {
    charge: number;
    multiplicity: number;
    onChargeChange: (value: number) => void;
    onMultiplicityChange: (value: number) => void;
    /** Prefilled from a parsed Gaussian input/output when available. */
    disabled?: boolean;
}

export const ChargeMultiplicityCard: React.FC<ChargeMultiplicityCardProps> = ({
    charge,
    multiplicity,
    onChargeChange,
    onMultiplicityChange,
    disabled
}) => {
    const { t } = useTranslation('common');

    const stepper = (
        value: number,
        set: (next: number) => void,
        min: number | undefined
    ): React.ReactNode => (
        <div className="aitomol-stepper">
            <CaretUpIcon onClick={() => !disabled && set(value + 1)} />
            <CaretDownIcon onClick={() => !disabled && set(min === undefined ? value - 1 : Math.max(min, value - 1))} />
        </div>
    );

    return (
        <div className="aitomol-panel">
            <div className="aitomol-panel-title">{t('aitomol.electronic_state')}</div>
            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.charge')}</span>
                <div className="aitomol-input jsmol-input">
                    <InputNumber
                        size="small"
                        disabled={disabled}
                        value={charge}
                        controls={false}
                        onChange={(value) => onChargeChange(value ?? 0)}
                        style={{ width: 44 }}
                    />
                </div>
                {stepper(charge, onChargeChange, undefined)}
            </div>
            <div className="aitomol-row">
                <span className="aitomol-label">{t('aitomol.multiplicity')}</span>
                <div className="aitomol-input jsmol-input">
                    <InputNumber
                        size="small"
                        disabled={disabled}
                        value={multiplicity}
                        min={1}
                        controls={false}
                        onChange={(value) => onMultiplicityChange(value ?? 1)}
                        style={{ width: 44 }}
                    />
                </div>
                {stepper(multiplicity, onMultiplicityChange, 1)}
            </div>
        </div>
    );
};

const CaretUpIcon: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <span className="aitomol-caret" onClick={onClick} role="button" tabIndex={-1}>
        <svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
            <path d="M0 6 L5 0 L10 6 Z" fill="currentColor" />
        </svg>
    </span>
);

const CaretDownIcon: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <span className="aitomol-caret" onClick={onClick} role="button" tabIndex={-1}>
        <svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">
            <path d="M0 0 L5 6 L10 0 Z" fill="currentColor" />
        </svg>
    </span>
);

export interface CalculationFloatButtonsProps {
    path?: string;
    charge: number;
    multiplicity: number;
    isFullScreen: boolean;
    onToggleFullScreen: () => void;
    animationPlaying: boolean;
    animationDisabled: boolean;
    onToggleAnimation: () => void;
}

export const CalculationFloatButtons: React.FC<CalculationFloatButtonsProps> = ({
    path,
    charge,
    multiplicity,
    isFullScreen,
    onToggleFullScreen,
    animationPlaying,
    animationDisabled,
    onToggleAnimation
}) => {
    const { t } = useTranslation('common');
    const { submit, submitting } = useCalculationJob(path);
    const [open, setOpen] = React.useState(false);

    return (
        <>
            <FloatButton.Group className="aitomol-floats absolute right-5 bottom-28 z-30">
                <Tooltip title={t('aitomol.play_animation')} placement="left">
                    <FloatButton
                        onClick={animationDisabled ? undefined : onToggleAnimation}
                        className={animationDisabled ? 'aitomol-float-disabled' : undefined}
                        icon={animationPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                    />
                </Tooltip>
                <Tooltip title={isFullScreen ? t('aitomol.exit_fullscreen') : t('aitomol.fullscreen')} placement="left">
                    <FloatButton
                        icon={isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                        onClick={onToggleFullScreen}
                    />
                </Tooltip>
            </FloatButton.Group>

            <FloatButton.Group
                className="aitomol-floats absolute right-5 bottom-12 z-30"
                trigger="click"
                placement="left"
                open={open}
                onClick={() => setOpen(!open)}
                icon={submitting ? <LoadingOutlined /> : <CaretRightOutlined />}
            >
                {CALCULATIONS.map((item) => (
                    <FloatButton
                        key={item.type}
                        shape="square"
                        description={item.label}
                        onClick={submitting || !path ? undefined : () => void submit({ type: item.type, charge, multiplicity })}
                    />
                ))}
            </FloatButton.Group>
        </>
    );
};